import { useEffect } from "react";
import ShreveportHeroSection from "./components/HeroSection";
import ShreveportStickyNav from "./components/StickyNav";
import ShreveportProcessingTimeSection from "./components/ProcessingTimeSection";
import ShreveportServicesSection from "./components/ServicesSection";
import ShreveportLocalAreaSection from "./components/LocalAreaSection";
import ShreveportTravelTimingSection from "./components/TravelTimingSection";
import ShreveportDestinationsSection from "./components/DestinationsSection";
import ShreveportTestimonialsSection from "./components/TestimonialsSection";
import ShreveportAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import ShreveportComparisonSection from "./components/ComparisonSection";
import ShreveportMistakesSection from "./components/MistakesSection";
import ShreveportHowItWorksSection from "./components/HowItWorksSection";
import ShreveportFAQSection, { faqs } from "./components/FAQSection";
import ShreveportHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import ShreveportFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function ShreveportPage() {
  useEffect(() => {
    document.title = "Emergency Passport Shreveport, LA — Expedited Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/shreveport/";
    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Shreveport, LA — as fast as 24 hours. Barksdale AFB families, casino industry, and oil workers served. A+ BBB rated, registered State Dept. courier serving Shreveport, Bossier City, and Northwest Louisiana.");
    setMeta("geo.placename", "Shreveport, Louisiana");
    setMeta("geo.region", "US-LA");
    setMeta("geo.position", "32.5252;-93.7502");
    setMeta("ICBM", "32.5252, -93.7502");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens." },
      { name: "New Passport", description: "First-time passport application assistance." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service." },
      { name: "Child Passport", description: "Passport services for minors under 16." },
      { name: "Name Change", description: "Passport correction and name change service." },
      { name: "Second Passport & Damaged", description: "Second passport issuance and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Louisiana", item: "https://fastpassportcenter.com/louisiana/" },
          { "@type": "ListItem", position: 3, name: "Shreveport Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Emergency and expedited passport services for Shreveport, Bossier City, and Northwest Louisiana travelers.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: { "@type": "City", name: "Shreveport", containedInPlace: { "@type": "State", name: "Louisiana" } },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1200" },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: { "@type": "LocalBusiness", name: "Fast Passport Center" },
        areaServed: { "@type": "City", name: "Shreveport" },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: { "@type": "Answer", text: faq.a },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      document.getElementById("structured-data")?.remove();
      document.querySelector('link[rel="canonical"]')?.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <ShreveportHeroSection />
      <ShreveportStickyNav />
      <ShreveportProcessingTimeSection />
      <ShreveportServicesSection />
      <ShreveportLocalAreaSection />
      <ShreveportTravelTimingSection />
      <ShreveportDestinationsSection />
      <ShreveportTestimonialsSection />
      <ShreveportAcceptanceFacilitiesSection />
      <ShreveportComparisonSection />
      <ShreveportMistakesSection />
      <ShreveportHowItWorksSection />
      <ShreveportFAQSection />
      <ShreveportHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/shreveport" state="LA" />
      <ShreveportFinalCTA />
    </div>
  );
}